<script setup lang="ts">
import { BookOpen, ExternalLink, Zap, FileText, Repeat, CheckCircle, FileCode, Database } from 'lucide-vue-next'
import PublicLayout from '@/components/PublicLayout.vue'
</script>

<template>
  <PublicLayout>
    <div class="max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div class="space-y-6 sm:space-y-8">
          <!-- Header -->
          <div class="text-center pt-4 sm:pt-8">
            <h1 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-3">Welcome to AutomaDoc</h1>
            <p class="text-lg sm:text-xl text-gray-600">Automate your document generation with Lark Base</p>
          </div>

          <!-- Main Description -->
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <p class="text-gray-800 leading-relaxed">
              AutomaDoc helps you generate professional documents from your Lark Base data automatically.
              Use Word templates with variable placeholders, support for loops and conditionals, and generate
              hundreds of documents at once.
            </p>
          </div>

          <!-- Features Section - Prominent -->
          <div id="features" class="space-y-4 sm:space-y-6 pt-4">
            <div class="text-center">
              <h2 class="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">Powerful Features</h2>
              <p class="text-sm sm:text-base text-gray-600">Everything you need to automate document generation</p>
            </div>

            <!-- Main Features Grid -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Feature 1: Simple Templates -->
              <div class="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-300 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <div class="flex items-start gap-4">
                  <div class="bg-blue-600 p-3 rounded-lg">
                    <FileText :size="24" class="text-white" />
                  </div>
                  <div class="flex-1">
                    <h3 class="text-xl font-bold text-gray-800 mb-2">Simple Word Templates</h3>
                    <p class="text-gray-700 mb-3">
                      Create templates using familiar Word documents. Just use <code class="bg-white px-2 py-0.5 rounded text-sm font-mono">{variable}</code> placeholders for data from your Lark Base.
                    </p>
                    <ul class="space-y-1 text-sm text-gray-600">
                      <li class="flex items-center gap-2">
                        <CheckCircle :size="16" class="text-blue-600" />
                        No complex syntax to learn
                      </li>
                      <li class="flex items-center gap-2">
                        <CheckCircle :size="16" class="text-blue-600" />
                        Support for nested fields and relationships
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- Feature 2: Bulk Generation -->
              <div class="bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-300 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <div class="flex items-start gap-4">
                  <div class="bg-purple-600 p-3 rounded-lg">
                    <Zap :size="24" class="text-white" />
                  </div>
                  <div class="flex-1">
                    <h3 class="text-xl font-bold text-gray-800 mb-2">Lightning-Fast Bulk Generation</h3>
                    <p class="text-gray-700 mb-3">
                      Generate hundreds of documents with a single click. Perfect for invoices, contracts, certificates, and more.
                    </p>
                    <ul class="space-y-1 text-sm text-gray-600">
                      <li class="flex items-center gap-2">
                        <CheckCircle :size="16" class="text-purple-600" />
                        Process entire tables at once
                      </li>
                      <li class="flex items-center gap-2">
                        <CheckCircle :size="16" class="text-purple-600" />
                        Auto-save to Lark attachments (Pro)
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- Feature 3: Advanced Logic -->
              <div class="bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-300 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <div class="flex items-start gap-4">
                  <div class="bg-green-600 p-3 rounded-lg">
                    <Repeat :size="24" class="text-white" />
                  </div>
                  <div class="flex-1">
                    <h3 class="text-xl font-bold text-gray-800 mb-2">Loops & Conditionals</h3>
                    <p class="text-gray-700 mb-3">
                      Use advanced templating logic to handle complex scenarios like repeating items, conditional sections, and dynamic formatting.
                    </p>
                    <ul class="space-y-1 text-sm text-gray-600">
                      <li class="flex items-center gap-2">
                        <CheckCircle :size="16" class="text-green-600" />
                        Iterate over multi-select and linked records
                      </li>
                      <li class="flex items-center gap-2">
                        <CheckCircle :size="16" class="text-green-600" />
                        Show/hide sections based on conditions
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- Feature 4: Seamless Integration -->
              <div class="bg-gradient-to-br from-orange-50 to-orange-100 border-2 border-orange-300 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <div class="flex items-start gap-4">
                  <div class="bg-orange-600 p-3 rounded-lg">
                    <Database :size="24" class="text-white" />
                  </div>
                  <div class="flex-1">
                    <h3 class="text-xl font-bold text-gray-800 mb-2">Deep Lark Integration</h3>
                    <p class="text-gray-700 mb-3">
                      Works natively inside Lark Base. Access all field types, formatting, and relationships automatically.
                    </p>
                    <ul class="space-y-1 text-sm text-gray-600">
                      <li class="flex items-center gap-2">
                        <CheckCircle :size="16" class="text-orange-600" />
                        All Lark field types supported
                      </li>
                      <li class="flex items-center gap-2">
                        <CheckCircle :size="16" class="text-orange-600" />
                        Currency, dates, and number formatting
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <!-- Additional Features Banner -->
            <div class="bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 border border-indigo-200 rounded-xl p-6">
              <h3 class="text-lg font-semibold text-gray-800 mb-4 text-center">Even More Features</h3>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div class="flex items-start gap-3">
                  <FileCode :size="20" class="text-indigo-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 class="font-semibold text-gray-800 text-sm">PDF Export</h4>
                    <p class="text-xs text-gray-600">Convert to PDF automatically (Pro)</p>
                  </div>
                </div>
                <div class="flex items-start gap-3">
                  <FileText :size="20" class="text-purple-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 class="font-semibold text-gray-800 text-sm">Dynamic Filenames</h4>
                    <p class="text-xs text-gray-600">Use data fields in output names</p>
                  </div>
                </div>
                <div class="flex items-start gap-3">
                  <CheckCircle :size="20" class="text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 class="font-semibold text-gray-800 text-sm">Generation History</h4>
                    <p class="text-xs text-gray-600">Track all generated documents</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Quick Example -->
          <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h3 class="text-lg font-semibold text-gray-800 mb-3">Quick Example</h3>
            <div class="space-y-2">
              <p class="text-sm text-gray-700">Create a Word template with variables:</p>
              <code class="block bg-white px-3 py-2 rounded border border-yellow-300 font-mono text-sm">
                Dear {Customer_Name},<br/>
                Your order {Order_Number} for {Product_Name} is ready!
              </code>
              <p class="text-sm text-gray-600 mt-2">
                → AutomaDoc replaces the variables with actual data from your Lark Base records
              </p>
            </div>
          </div>

          <!-- Setup Guide Card -->
          <div class="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-6">
            <div class="flex items-start gap-4">
              <div class="bg-white p-3 rounded-lg border border-indigo-300">
                <BookOpen :size="32" class="text-indigo-600" />
              </div>
              <div class="flex-1">
                <h2 class="text-xl font-bold text-gray-800 mb-2">Getting Started</h2>
                <p class="text-gray-700 mb-4">
                  To use AutomaDoc, you'll need to add it as an extension to your Lark Base.
                  Follow our comprehensive installation and setup guide to get started in minutes.
                </p>
                <a
                  href="https://ksi-digital.jp.larksuite.com/wiki/JRa7wPLwjiZFLWk1hFijJRQppxg"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  <BookOpen :size="18" />
                  Open Installation Guide
                  <ExternalLink :size="16" />
                </a>
              </div>
            </div>
          </div>
        </div>
    </div>
  </PublicLayout>
</template>
